import React, { useEffect, useState } from "react";
import { Plus, Building2, UserCog, ScrollText, Power, Activity } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth";
import { Button, Card, Input, Field, Select, Modal, Badge, Spinner, EmptyState } from "../../components/ui";
import { toast } from "sonner";

export function ClinicsPage() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "" });
  const load = () => api.get("/clinics").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.name) return toast.error("Nom requis");
    try { await api.post("/clinics", f); toast.success("Cabinet créé"); setOpen(false); setF({ name: "", phone: "", email: "", address: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.post(`/clinics/${id}/status`, { status }); load(); };
  const toggleHill = async (c) => { await api.post(`/clinics/${c.id}/hilldt`, { enabled: !c.hill_dt_enabled }); toast.success("Module Hill DT mis à jour"); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <h1 className="font-head font-bold text-navy text-2xl flex items-center gap-2"><Building2 className="text-teal" /> {t("clinics")}</h1>
        <Button className="ms-auto" onClick={() => setOpen(true)} data-testid="new-clinic-btn"><Plus size={16} /> Créer</Button>
      </div>
      {!items ? <Spinner /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <Card key={c.id} className="p-5">
              <div className="flex items-center gap-2 mb-1">
                <Badge tone={c.status === "active" ? "green" : "warn"}>{c.status}</Badge>
                {c.hill_dt_enabled && <Badge tone="teal"><Activity size={11} /> Hill DT</Badge>}
              </div>
              <h3 className="font-head font-semibold text-navy">{c.name}</h3>
              <p className="text-sm text-slate-400">{c.phone} · {c.email}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => toggleHill(c)} data-testid={`clinic-hill-${c.id}`}>Hill DT {c.hill_dt_enabled ? "off" : "on"}</Button>
                {c.status === "active" ?
                  <Button size="sm" variant="warn" onClick={() => setStatus(c.id, "suspended")}>Suspendre</Button> :
                  <Button size="sm" variant="primary" onClick={() => setStatus(c.id, "active")}>Réactiver</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau cabinet">
        <div className="space-y-3">
          <Field label="Nom"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="clinic-name" /></Field>
          <Field label={t("phone")}><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          <Field label={t("email")}><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label={t("address")}><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={create} data-testid="clinic-submit">{t("create")}</Button></div>
        </div>
      </Modal>
    </div>
  );
}

export function UsersPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "professional", clinic_id: "" });
  const load = () => api.get("/users").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); if (user.role === "superadmin") api.get("/clinics").then((r) => setClinics(r.data)).catch(() => {}); }, []);
  const create = async () => {
    if (!f.name || !f.email || f.password.length < 8) return toast.error("Nom, courriel et mot de passe (8+) requis");
    try { await api.post("/users", f); toast.success("Utilisateur créé"); setOpen(false); setF({ name: "", email: "", password: "", role: "professional", clinic_id: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = async (id) => { await api.post(`/users/${id}/toggle`); load(); };
  const roles = user.role === "superadmin" ? ["clinic_admin", "professional", "assistant"] : ["professional", "assistant"];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <h1 className="font-head font-bold text-navy text-2xl flex items-center gap-2"><UserCog className="text-teal" /> {t("users")}</h1>
        <Button className="ms-auto" onClick={() => setOpen(true)} data-testid="new-user-btn"><Plus size={16} /> Ajouter</Button>
      </div>
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title={t("no_data")} /> : (
        <Card className="divide-y divide-slate-100">
          {items.map((u) => (
            <div key={u.id} className="flex items-center gap-3 px-5 py-3">
              <div className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center text-xs font-bold">{u.name[0]}</div>
              <div><div className="text-sm font-medium text-navy">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
              <Badge tone="navy" className="ms-2">{t(`role_${u.role}`)}</Badge>
              <div className="ms-auto flex items-center gap-2">
                <Badge tone={u.active ? "green" : "default"}>{u.active ? "Actif" : "Inactif"}</Badge>
                <Button size="sm" variant="ghost" onClick={() => toggle(u.id)} data-testid={`toggle-user-${u.id}`}><Power size={14} /></Button>
              </div>
            </div>
          ))}
        </Card>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="Nouvel utilisateur">
        <div className="space-y-3">
          <Field label="Nom"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></Field>
          <Field label={t("email")}><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></Field>
          <Field label={t("password")}><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="8 caractères min." data-testid="user-password" /></Field>
          <Field label="Rôle"><Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role">
            {roles.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}</Select></Field>
          {user.role === "superadmin" && <Field label={t("clinics")}><Select value={f.clinic_id} onChange={(e) => setF({ ...f, clinic_id: e.target.value })}>
            <option value="">—</option>{clinics.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>}
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button><Button onClick={create} data-testid="user-submit">{t("create")}</Button></div>
        </div>
      </Modal>
    </div>
  );
}

export function AuditPage() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/audit").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="space-y-5">
      <h1 className="font-head font-bold text-navy text-2xl flex items-center gap-2"><ScrollText className="text-teal" /> {t("audit")}</h1>
      {!items ? <Spinner /> : items.length === 0 ? <EmptyState title={t("no_data")} /> : (
        <Card className="divide-y divide-slate-100">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <Badge tone="teal">{a.action}</Badge>
              <span className="text-slate-600">{a.user_email}</span>
              {a.entity_type && <span className="text-xs text-slate-400">{a.entity_type}</span>}
              <span className="ms-auto text-xs text-slate-400">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
